// CĂILE DE EVACUARE ale subsolului (NP 127:2009 cap. VII), peste planșa de
// parcaj: pentru fiecare ieșire SAS aleasă, TRASEUL CEL MAI LUNG — de la ușa
// SAS, pe marginea sau pe axa aleilor, până în fața locului cel mai
// îndepărtat —, cu distanța scrisă la capăt. Atât: toate rutele, graful și
// jurnalele stau în pagina de depanare a pasului, nu în document.
//
// Traseele vin din `diagnostics.evacuare.niveluri[].trasee`, scrise de
// `architecture/evacuare/pas.py` în coordonatele ABSOLUTE ale documentului,
// deci trec prin aceeași traducere ca schița și adnotările (`punctInCadru`).
//
// DESENUL E CEL DE PE PLANȘA DE ARHITECTURĂ: pe alee traseul e o linie
// punctată verde cu săgeți spre ieșire; unde omul iese dintre mașini — de la
// ușa SAS prin rândul de locuri până la alee, prin coridorul pietonal dintre
// două alei, până în fața locului — se desenează fâșia de 0,90 m pe care o
// cere trecerea, centrată pe traseu. Documentul nu spune pe ce bucată e
// coridor, iar geometria coridoarelor nu e în el; bucata se citește de aici,
// tăind fiecare segment la marginea benzii aleilor nivelului (banda = axa cu
// lățimea ei, două inegalități liniare în parametrul segmentului) — câteva
// zeci de operații pe document, nu un calcul geometric.

import { useMemo } from "react";
import { DECOR } from "./glyphs";
import type { StoreyShape } from "../canonical";

export type TraseuEvacuare = {
  iesire: string; loc: string; distanta_m: number; puncte: number[][];
};

/** CALEA MINIMĂ A UNUI LOC, cum o întoarce `api.rutaEvacuarii` (artefactul
 *  `evacuare_rute`, scris de `architecture/evacuare/pas.rute_pe_loc`), ca
 *  traseu de desenat — sau `null`: loc fără rută, ori o polilinie dintr-un
 *  singur punct, care n-are ce desena. */
export function traseulLocului(loc: string,
                               r: { iesire: string; distanta_m: number; puncte: number[][] } | null | undefined,
): TraseuEvacuare | null {
  if (!r || !Array.isArray(r.puncte) || r.puncte.length < 2) return null;
  return { iesire: r.iesire, loc, distanta_m: r.distanta_m, puncte: r.puncte };
}

/** Verdele căilor de evacuare de pe planșa de arhitectură de referință
 *  (cel al săgeților și al fâșiei de acolo, cel închis). */
const CULOARE = "#00803f";
/** Calea LOCULUI ALES (clic pe loc): alt ton decât traseele cele mai lungi,
 *  ca să nu se confunde cu ele când trec pe aceleași alei. */
const CULOARE_ALES = "#0b5cd5";
/** Lățimea coridorului pietonal dintre locuri: aceeași cu
 *  `architecture/evacuare/coridoare.LATIME_MIN`, lățimea minimă cerută de
 *  NP 127 pentru trecerea pietonală. */
export const LATIME_CORIDOR_M = 0.9;
/** Cât de departe de banda aleii traseul mai e «pe alee»: fața locului e la
 *  0,15 m dincolo de margine, iar o săritură de rotunjire nu trebuie să-i
 *  pună fâșie. O trecere adevărată intră cel puțin 2 m între locuri. */
const TOLERANTA_M = 0.3;
/** O bucată de coridor mai scurtă de atât nu primește fâșie: ar fi o pată. */
const FASIE_MIN_M = 0.2;
/** Săgețile se repetă pe bucățile lungi de alee, cam la atât; sub
 *  `SAGEATA_MIN_M` bucata nu primește niciuna (ar acoperi-o colțurile). */
const PAS_SAGEATA_M = 10;
const SAGEATA_MIN_M = 2.5;

/** Banda unei alei: axa, ca reper (origine, versor, lungime), și semilățimea. */
type Banda = { o: number[]; ux: number; uy: number; L: number; semi: number };

const benzile = (alei: StoreyShape[]): Banda[] => alei
  .filter((a) => a.axis && a.axis.length >= 2 && (a.width_m ?? 0) > 0)
  .map((a) => {
    const o = a.axis![0], c = a.axis![a.axis!.length - 1];
    const L = Math.hypot(c[0] - o[0], c[1] - o[1]) || 1;
    return { o, ux: (c[0] - o[0]) / L, uy: (c[1] - o[1]) / L, L, semi: a.width_m! / 2 + TOLERANTA_M };
  });

/** Intervalul `[t0, t1]` din segmentul `a → b` (t în [0, 1]) care cade în
 *  bandă, sau `null`. În reperul axei, capetele au coordonata de-a lungul ei
 *  și pe perpendiculară; amândouă sunt liniare în t, deci fiecare condiție
 *  («între margini», «între capetele axei») taie un interval. */
function inBanda(a: number[], b: number[], bz: Banda): [number, number] | null {
  const rel = (p: number[]) => {
    const px = p[0] - bz.o[0], py = p[1] - bz.o[1];
    return [px * bz.ux + py * bz.uy, -px * bz.uy + py * bz.ux];
  };
  const [sa, na] = rel(a), [sb, nb] = rel(b);
  let t0 = 0, t1 = 1;
  const taie = (fa: number, fb: number, lo: number, hi: number) => {
    const df = fb - fa;
    if (Math.abs(df) < 1e-9) { if (fa < lo || fa > hi) { t0 = 1; t1 = 0; } return; }
    let u0 = (lo - fa) / df, u1 = (hi - fa) / df;
    if (u0 > u1) [u0, u1] = [u1, u0];
    t0 = Math.max(t0, u0); t1 = Math.min(t1, u1);
  };
  taie(na, nb, -bz.semi, bz.semi);
  taie(sa, sb, -TOLERANTA_M, bz.L + TOLERANTA_M);
  return t1 > t0 ? [t0, t1] : null;
}

export type Bucata = { alee: boolean; t0: number; t1: number };

/** Bucățile segmentului `a → b`, în ordine: pe alee (în banda vreunei alei)
 *  sau prin coridor (în afara tuturor). Acoperă tot [0, 1]. */
export function bucatileSegmentului(a: number[], b: number[], benzi: Banda[]): Bucata[] {
  const pe = benzi.map((bz) => inBanda(a, b, bz))
    .filter((x): x is [number, number] => x != null)
    .sort((x, y) => x[0] - y[0]);
  const uni: [number, number][] = [];
  for (const [s, e] of pe) {
    const u = uni[uni.length - 1];
    if (u && s <= u[1]) u[1] = Math.max(u[1], e); else uni.push([s, e]);
  }
  const out: Bucata[] = [];
  let t = 0;
  for (const [s, e] of uni) {
    if (s > t) out.push({ alee: false, t0: t, t1: s });
    out.push({ alee: true, t0: s, t1: e });
    t = e;
  }
  if (t < 1) out.push({ alee: false, t0: t, t1: 1 });
  return out;
}

/** Bucățile unui traseu întreg, cu capetele lor în coordonate. */
export function bucatileTraseului(puncte: number[][], alei: StoreyShape[]) {
  const benzi = benzile(alei);
  const out: { alee: boolean; p: number[]; q: number[] }[] = [];
  for (let i = 0; i + 1 < puncte.length; i++) {
    const a = puncte[i], b = puncte[i + 1];
    const la = (t: number) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    for (const { alee, t0, t1 } of bucatileSegmentului(a, b, benzi)) {
      out.push({ alee, p: la(t0), q: la(t1) });
    }
  }
  return out;
}

/** Săgeata spre ieșire: triunghi de 0,6 m, cu vârful în sensul `[dx, dy]` —
 *  cât cel de pe planșa de referință, nu mai lat decât un sfert de loc. */
const sageata = (x: number, y: number, dx: number, dy: number): string => {
  const nx = -dy, ny = dx;
  return [[x + dx * 0.36, y + dy * 0.36], [x - dx * 0.24 + nx * 0.27, y - dy * 0.24 + ny * 0.27],
          [x - dx * 0.24 - nx * 0.27, y - dy * 0.24 - ny * 0.27]]
    .map((p) => p.join(",")).join(" ");
};

export default function EvacuareTrasee({ trasee, la, alei, ales = false }: {
  trasee: TraseuEvacuare[];
  /** traducerea unui punct absolut în cadrul desenului (v. `punctInCadru`) */
  la: (p: number[]) => number[];
  /** aleile nivelului desenat, în cadrul desenului (`StoreyPlan.lanes`) */
  alei: StoreyShape[];
  /** calea locului ales (clic pe loc): albastru, linie plină, mai groasă */
  ales?: boolean;
}) {
  const culoare = ales ? CULOARE_ALES : CULOARE;
  const desenate = useMemo(() => trasee.map((t) => {
    const pts = t.puncte.map((p) => la(p));
    // fâșiile: șirurile de bucăți «coridor» lipite cap la cap, fiecare o polilinie
    const fasii: number[][][] = [];
    const sageti: string[] = [];
    for (const { alee, p, q } of bucatileTraseului(pts, alei)) {
      const L = Math.hypot(q[0] - p[0], q[1] - p[1]);
      if (!alee) {
        const u = fasii[fasii.length - 1];
        const cap = u?.[u.length - 1];
        if (cap && Math.hypot(cap[0] - p[0], cap[1] - p[1]) < 1e-6) u.push(q);
        else if (L >= FASIE_MIN_M) fasii.push([p, q]);
        continue;
      }
      // traseul pleacă de la ușa SAS, deci ieșirea e ÎNAPOI pe bucată
      if (L < SAGEATA_MIN_M) continue;
      const dx = (p[0] - q[0]) / L, dy = (p[1] - q[1]) / L;
      const n = Math.max(1, Math.floor(L / PAS_SAGEATA_M));
      for (let k = 0; k < n; k++) {
        const f = (k + 0.5) / n;
        sageti.push(sageata(p[0] + (q[0] - p[0]) * f, p[1] + (q[1] - p[1]) * f, dx, dy));
      }
    }
    // SĂGEATA DE LA UȘĂ (Oana, 20.10.2026): în locul bulinei pline, care acoperea
    // ușa EI 60-C de pe planșă, o săgeată ca cele de pe traseu, cu vârful spre
    // ușă, la o jumătate de metru înaintea ei pe prima bucată a traseului.
    if (pts.length >= 2) {
      const [a, b] = [pts[0], pts[1]];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L > 1e-6) {
        const dx = (a[0] - b[0]) / L, dy = (a[1] - b[1]) / L;
        const f = Math.min(0.5, L / 2) / L;
        sageti.push(sageata(a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, dx, dy));
      }
    }
    return { t, pts, fasii, sageti };
  }), [trasee, la, alei]);
  if (!trasee.length) return null;
  return (
    <g style={DECOR} data-strat={ales ? "evacuare-loc" : "evacuare"}>
      {desenate.map(({ t, pts, fasii, sageti }) => {
        if (pts.length < 2) return null;
        const [ex, ey] = pts[pts.length - 1];
        return (
          <g key={`${t.iesire}-${t.loc}`}>
            {/* fâșia de 0,90 m prin coridor: lățimea e în metri, se mărește cu planul */}
            {fasii.map((f, i) => (
              <polyline key={i} points={f.map((p) => p.join(",")).join(" ")}
                        fill="none" stroke={culoare} strokeWidth={LATIME_CORIDOR_M}
                        strokeOpacity={0.3} strokeLinejoin="miter" strokeLinecap="butt" />
            ))}
            <polyline points={pts.map((p) => p.join(",")).join(" ")}
                      fill="none" stroke={culoare} strokeWidth={ales ? 3.2 : 2.2}
                      strokeDasharray={ales ? undefined : "7 5"}
                      strokeLinejoin="round" strokeLinecap="round" opacity={0.9}
                      vectorEffect="non-scaling-stroke" />
            {sageti.map((s, i) => (
              <polygon key={i} points={s} fill={culoare} stroke="#fff" strokeWidth={0.06} />
            ))}
            {/* ușa SAS: doar săgeata finală (în `sageti`), fără cerc — cercul
                acoperea ușa; capătul: punct la fața locului, cu distanța — doar
                ea: id-ul ieșirii e intern (clădire, latură, poziție), nu spune
                nimic celui care citește planșa */}
            <circle cx={ex} cy={ey} r={0.3} fill="#fff" stroke={culoare} strokeWidth={0.12} />
            <text x={ex} y={ey - 0.9} fontSize={ales ? 1.5 : 1.2} fontWeight={700} fill={culoare}
                  textAnchor="middle"
                  style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 0.35 }}>
              {`${t.distanta_m.toFixed(1)} m`}
            </text>
          </g>
        );
      })}
    </g>
  );
}
